import React, { FC, useEffect, useState } from 'react';

import Box from '@mui/material/Box';

import {
  ContactFormWrapper,
  EmailInput,
  Button,
  ContactFormText,
  FormControlContainer,
  EmailInputContainer,
  ThanksText,
  WebWrapper,
  ContactFormDescription,
} from './ContactForm.style';

import { validateEmail } from './utils';
import { sendContactForm } from '../../client';
import { useIsMobile } from 'hooks/useIsMobile';

import TopWeb from 'assets/contact-form/image 169.png';
import BottomWeb from 'assets/contact-form/image 170.png';
import { getPageTag } from 'utils/getPageTag';

const INTERVAL = 5000;

type ContactFormType = 'default' | 'auto-docking' | 'pipeline-data';

interface ContactFormProps {
  type?: ContactFormType;
}

interface TypeData {
  title: string;
  description: string;
}

const data = new Map<ContactFormType, TypeData>([
  [
    'default',
    {
      title: 'Discover the benefits of synthetic data and simulation',
      description: '',
    },
  ],
  [
    'auto-docking',
    {
      title: 'Ready to Revolutionize Your Maritime AI Training?',
      description:
        "Don't miss this opportunity to gain a competitive edge. Be one of the first 10 participants to explore AILiveSim’s intelligent simulation platform.",
    },
  ],
  [
    'pipeline-data',
    {
      title: 'Ready to Revolutionize Your Maritime AI Training?',
      description:
        'Secure your spot in this limited beta program. Gain a competitive advantage by leveraging intelligent simulation to build more robust and accurate maritime AI, faster than ever before. Help shape the future of AI training in the maritime domain.',
    },
  ],
]);

export const ContactForm: FC<ContactFormProps> = ({ type = 'default' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const isMobile = useIsMobile();
  const { description, title } = data.get(type) as TypeData;
  const buttonLabel = type === 'default' ? 'Take the first step' : 'Apply now';
  const tagButtonLabel = type === 'default' ? 'contact-us' : 'apply-now';
  const pageTag = getPageTag(location.pathname, tagButtonLabel);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmail(e.currentTarget.value);
  };

  const onClick = () => {
    if (!validateEmail(email)) {
      setIsValid(false);
      return '';
    }

    setIsValid(true);
    setIsLoading(true);

    sendContactForm(email, pageTag)
      .then(() => {
        setIsLoading(false);
        setIsCompleted(true);
      })
      .catch(() => {
        setIsLoading(false);
        setIsValid(false);
      });
  };

  useEffect(() => {
    if (isCompleted) {
      setTimeout(() => {
        setIsCompleted(false);
      }, INTERVAL);
    }
  }, [isCompleted]);

  return (
    <ContactFormWrapper id="contact-us" component="form" onSubmit={onClick} mt="auto">
      <WebWrapper left="0" top="0">
        <img src={TopWeb} alt="top web" />
      </WebWrapper>

      <WebWrapper bottom="-30%" right="0">
        <img src={BottomWeb} alt="bottom web" />
      </WebWrapper>

      {isCompleted ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: '100%', width: '100%' }}
        >
          <ThanksText>Thanks for your subscription!</ThanksText>
        </Box>
      ) : (
        <>
          <ContactFormText>{title}</ContactFormText>
          {description && <ContactFormDescription>{description}</ContactFormDescription>}
          <FormControlContainer>
            <EmailInputContainer>
              <EmailInput
                error={!isValid}
                disabled={isLoading}
                fullWidth
                value={email}
                placeholder="Email address"
                onChange={onChange}
                aria-invalid={!isValid}
                aria-describedby="email-error"
              />
              {!isValid && (
                <span id="email-error" className="sr-only">
                  Please enter a valid email address.
                </span>
              )}

              {!isMobile && (
                <Button disabled={isLoading} onClick={onClick} aria-label={buttonLabel}>
                  {buttonLabel}
                </Button>
              )}
            </EmailInputContainer>

            {isMobile && (
              <Button disabled={isLoading} onClick={onClick} aria-label={buttonLabel}>
                {buttonLabel}
              </Button>
            )}
          </FormControlContainer>
        </>
      )}
    </ContactFormWrapper>
  );
};
